import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  customerOrders, supplierOrders,
  type CustomerOrderItem, type SupplierOrderItem,
  type OrderKind,
} from '@/api/orders';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import ProjectSelect from '@/components/ProjectSelect';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';

type Kind = 'customer' | 'supplier';

interface ItemRow {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  quantity_shipped?: string;
  quantity_received?: string;
  quantity_reserved?: string;
  price: string;
  vat_rate_id: string;
  amount_without_vat: string;
  vat_amount: string;
  total_amount: string;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  warehouse_id: string;
  delivery_date: string;
  comment: string;
  project_id: string | null;
  order_kind: OrderKind;
  items: ItemRow[];
  // ПРОДАЖ-розбивка Етап 1 (2026-06-03).
  incoterms: string;
  payment_terms_type: '' | 'PREPAY' | 'PARTIAL' | 'POSTPAY' | 'ON_DELIVERY';
  prepayment_percent: string;
  final_payment_days: string;
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  warehouse_id: '',
  delivery_date: '',
  comment: '',
  project_id: null,
  order_kind: 'ORDER',
  items: [],
  incoterms: '',
  payment_terms_type: '',
  prepayment_percent: '',
  final_payment_days: '',
};

const INCOTERMS_OPTIONS = [
  'EXW', 'FCA', 'FAS', 'FOB',
  'CFR', 'CIF', 'CPT', 'CIP',
  'DAP', 'DPU', 'DDP',
] as const;

const PAYMENT_TERMS_LABELS: Record<string, string> = {
  PREPAY: 'Повна передоплата',
  PARTIAL: 'Часткова передоплата',
  POSTPAY: 'Постоплата',
  ON_DELIVERY: 'При отриманні',
};

const newRow = (n: number, vatId: string): ItemRow => ({
  line_number: n, nomenclature_id: '', quantity: '0', price: '0',
  vat_rate_id: vatId, amount_without_vat: '0', vat_amount: '0', total_amount: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function OrderEditor({ kind }: { kind: Kind }) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  const isCustomer = kind === 'customer';
  // ПРОДАЖ-розбивка Етап 1 (2026-06-03): URL ?kind=PROFORMA_INVOICE з invoices сторінки.
  const urlKind = searchParams.get('kind') as OrderKind | null;
  // baseRoute визначається з URL pathname або URL kind param.
  const isInvoiceContext = urlKind === 'PROFORMA_INVOICE'
    || window.location.pathname.includes('-invoices');
  const baseRoute = isInvoiceContext
    ? (isCustomer ? '/customer-invoices' : '/supplier-invoices')
    : (isCustomer ? '/customer-orders' : '/supplier-orders');
  const partyLabel = isCustomer ? 'Покупець' : 'Постачальник';

  const queryKeySingle = isCustomer ? 'customer-order' : 'supplier-order';
  const queryKeyList = isCustomer ? 'customer-orders' : 'supplier-orders';

  const api = isCustomer ? customerOrders : supplierOrders;

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX α: фільтр org dropdown по per-kind permission
  const requiredPerm = isCustomer ? 'sales.order.manage' : 'purchases.order.manage';
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg(requiredPerm);
  const contractorsList = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const filteredContractors = useMemo(() => {
    if (!contractorsList.data) return [];
    return contractorsList.data.items.filter(
      (c) => isCustomer ? c.is_buyer : c.is_supplier,
    );
  }, [contractorsList.data, isCustomer]);
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 500 }),
  });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const docQuery = useQuery({
    queryKey: [queryKeySingle, id],
    queryFn: () => api.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });

  const defaultVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.rate_percent === 20);
    return v?.id ?? '';
  }, [vatList.data]);

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      // ПРОДАЖ-розбивка Етап 1: для нового документа беремо order_kind з URL.
      const initialKind: OrderKind = (urlKind === 'PROFORMA_INVOICE' || isInvoiceContext)
        ? 'PROFORMA_INVOICE'
        : 'ORDER';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        order_kind: initialKind,
        items: f.items.length ? f.items : [newRow(1, defaultVatId)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id,
        warehouse_id: d.warehouse_id ?? '',
        delivery_date: d.delivery_date ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        order_kind: d.order_kind ?? 'ORDER',
        incoterms: d.incoterms ?? '',
        payment_terms_type: (d.payment_terms_type ?? '') as FormState['payment_terms_type'],
        prepayment_percent: d.prepayment_percent ?? '',
        final_payment_days: d.final_payment_days !== undefined && d.final_payment_days !== null
          ? String(d.final_payment_days)
          : '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          quantity: String(it.quantity),
          quantity_shipped: 'quantity_shipped' in it ? String(it.quantity_shipped ?? '0') : undefined,
          quantity_received: 'quantity_received' in it ? String(it.quantity_received ?? '0') : undefined,
          quantity_reserved: 'quantity_reserved' in it ? String(it.quantity_reserved ?? '0') : undefined,
          price: String(it.price),
          vat_rate_id: it.vat_rate_id,
          amount_without_vat: String(it.amount_without_vat),
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs, defaultVatId]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalWithoutVat = form.items.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0);
  const totalVat = form.items.reduce((s, e) => s + Number(e.vat_amount || 0), 0);
  const totalAmount = totalWithoutVat + totalVat;

  function buildPayloadItems(): (CustomerOrderItem | SupplierOrderItem)[] {
    return form.items.map((it) => ({
      line_number: it.line_number,
      nomenclature_id: it.nomenclature_id,
      quantity: it.quantity,
      price: it.price,
      vat_rate_id: it.vat_rate_id,
      amount_without_vat: it.amount_without_vat,
      vat_amount: it.vat_amount,
      total_amount: it.total_amount,
    }));
  }

  const create = useMutation({
    mutationFn: () => api.create({
      organization_id: form.organization_id,
      // Auto-numbering: порожнє → backend згенерує через next_number().
      document_number: form.document_number || null,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      warehouse_id: form.warehouse_id || null,
      delivery_date: form.delivery_date || null,
      comment: form.comment || null,
      project_id: form.project_id,
      order_kind: form.order_kind,
      // ПРОДАЖ-розбивка Етап 1.
      incoterms: form.incoterms || null,
      payment_terms_type: form.payment_terms_type || null,
      prepayment_percent: form.prepayment_percent || null,
      final_payment_days: form.final_payment_days ? Number(form.final_payment_days) : null,
      items: buildPayloadItems() as never,
    } as never),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: [queryKeyList] });
      navigate(`${baseRoute}/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => api.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      warehouse_id: form.warehouse_id || null,
      delivery_date: form.delivery_date || null,
      comment: form.comment || null,
      project_id: form.project_id,
      order_kind: form.order_kind,
      // ПРОДАЖ-розбивка Етап 1.
      incoterms: form.incoterms || null,
      payment_terms_type: form.payment_terms_type || null,
      prepayment_percent: form.prepayment_percent || null,
      final_payment_days: form.final_payment_days ? Number(form.final_payment_days) : null,
      items: buildPayloadItems() as never,
    } as never),
    onSuccess: () => qc.invalidateQueries({ queryKey: [queryKeySingle, id] }),
    onError: (e) => setError(extractError(e)),
  });
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await api.create({
        organization_id: form.organization_id,
        // Auto-numbering: порожнє → backend згенерує через next_number().
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        contractor_id: form.contractor_id,
        contract_id: form.contract_id || null,
        warehouse_id: form.warehouse_id || null,
        delivery_date: form.delivery_date || null,
        comment: form.comment || null,
        project_id: form.project_id,
        order_kind: form.order_kind,
        // ПРОДАЖ-розбивка Етап 1.
        incoterms: form.incoterms || null,
        payment_terms_type: form.payment_terms_type || null,
        prepayment_percent: form.prepayment_percent || null,
        final_payment_days: form.final_payment_days ? Number(form.final_payment_days) : null,
        items: buildPayloadItems() as never,
      } as never);
      await api.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: [queryKeyList] });
      navigate(`${baseRoute}/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await api.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        contractor_id: form.contractor_id,
        contract_id: form.contract_id || null,
        warehouse_id: form.warehouse_id || null,
        delivery_date: form.delivery_date || null,
        comment: form.comment || null,
        project_id: form.project_id,
        order_kind: form.order_kind,
        // ПРОДАЖ-розбивка Етап 1.
        incoterms: form.incoterms || null,
        payment_terms_type: form.payment_terms_type || null,
        prepayment_percent: form.prepayment_percent || null,
        final_payment_days: form.final_payment_days ? Number(form.final_payment_days) : null,
        items: buildPayloadItems() as never,
      } as never);
      await api.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKeySingle, id] });
      qc.invalidateQueries({ queryKey: [queryKeyList] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => api.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKeySingle, id] });
      qc.invalidateQueries({ queryKey: [queryKeyList] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKeyList] });
      navigate(baseRoute);
    },
  });
  // ПРОДАЖ-розбивка Етап 5 (2026-06-03): закриття/відкриття замовлення.
  const close = useMutation({
    mutationFn: (reason: string | undefined) => api.close(id!, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKeySingle, id] });
      qc.invalidateQueries({ queryKey: [queryKeyList] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const reopen = useMutation({
    mutationFn: () => api.reopen(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKeySingle, id] });
      qc.invalidateQueries({ queryKey: [queryKeyList] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const closingStatus = docQuery.data?.closing_status ?? 'OPEN';
  const isClosed = closingStatus === 'CLOSED';

  function setItem(i: number, patch: Partial<ItemRow>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        const qty = Number(next.quantity) || 0;
        const price = Number(next.price) || 0;
        const vat = vatList.data?.find((v) => v.id === next.vat_rate_id);
        const rate = vat?.rate_percent ?? 0;
        if ('quantity' in patch || 'price' in patch || 'vat_rate_id' in patch) {
          const awv = qty * price;
          const vatAmt = (awv * rate) / 100;
          next.amount_without_vat = awv.toFixed(2);
          next.vat_amount = vatAmt.toFixed(2);
          next.total_amount = (awv + vatAmt).toFixed(2);
        }
        return next;
      }),
    }));
  }
  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1, defaultVatId)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  const progressColumn = isCustomer ? 'quantity_shipped' : 'quantity_received';
  const progressLabel = isCustomer ? 'Відвантажено' : 'Отримано';

  // UX α: warning якщо новий документ а user не має permission у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    const what = isCustomer ? 'замовлення покупця' : 'замовлення постачальнику';
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>Нове {what}</h2>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати {what} у жодній з ваших організацій
            ({totalAccessibleOrgs}). Зверніться до адміністратора tenant'а.
          </div>
          <button onClick={() => navigate(baseRoute)}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? (form.order_kind === 'PROFORMA_INVOICE'
                ? (isCustomer ? 'Новий рахунок на оплату' : 'Новий рахунок від постачальника')
                : (isCustomer ? 'Нове замовлення покупця' : 'Нове замовлення постачальнику'))
            : `${form.order_kind === 'PROFORMA_INVOICE'
                ? (isCustomer ? 'Рахунок на оплату' : 'Рахунок від постачальника')
                : (isCustomer ? 'Замовлення покупця' : 'Замовлення постачальнику')}: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
          {/* ПРОДАЖ-розбивка Етап 5: бейдж closing_status */}
          {isClosed && (
            <span
              style={{
                marginLeft: '0.75rem',
                padding: '0.15rem 0.5rem',
                background: '#dbeafe',
                color: '#1e40af',
                borderRadius: '4px',
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
              title={docQuery.data?.close_reason ?? undefined}
            >
              📦 ЗАКРИТО
              {docQuery.data?.closed_at && (
                <span style={{ marginLeft: 4, fontWeight: 400, opacity: 0.75 }}>
                  {new Date(docQuery.data.closed_at).toLocaleDateString('uk-UA')}
                </span>
              )}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate(baseRoute)}>Скасувати</button>
          {!isPosted && (
            <button
              className="primary"
              onClick={() => isNew ? create.mutate() : update.mutate()}
              disabled={
                create.isPending ||
                update.isPending ||
                !form.organization_id ||
                !form.contractor_id
              }
              title={
                !form.organization_id ? 'Спочатку оберіть юрособу'
                  : !form.contractor_id ? `Спочатку оберіть ${partyLabel.toLowerCase()}а`
                  : undefined
              }
            >
              Зберегти
            </button>
          )}
          {isNew && (
            <button
              className="primary"
              onClick={() => createAndPost.mutate()}
              disabled={
                create.isPending ||
                createAndPost.isPending ||
                !form.organization_id ||
                !form.contractor_id
              }
              title={
                !form.organization_id ? 'Спочатку оберіть юрособу'
                  : !form.contractor_id ? `Спочатку оберіть ${partyLabel.toLowerCase()}а`
                  : 'Зберігає та проводить документ'
              }
            >
              ▶ Провести
            </button>
          )}
          {!isNew && !isPosted && form.items.length > 0 && (
            <button
              className="primary"
              onClick={() => updateAndPost.mutate()}
              disabled={updateAndPost.isPending || update.isPending}
              title="Зберігає поточні зміни та проводить документ"
            >
              ▶ Провести
            </button>
          )}
          {!isNew && isPosted && (
            <button onClick={() => unpost.mutate()} disabled={unpost.isPending}>
              ◀ Розпровести
            </button>
          )}
          {/* ПРОДАЖ-розбивка Етап 5: закрити/відкрити */}
          {!isNew && !isClosed && (
            <button
              onClick={() => {
                const reason = window.prompt(
                  'Причина закриття (необов\'язково):',
                  '',
                );
                if (reason !== null) close.mutate(reason || undefined);
              }}
              disabled={close.isPending}
              title="Закрити замовлення вручну (наприклад, клієнт відмовився)"
            >
              ✓ Закрити
            </button>
          )}
          {!isNew && isClosed && (
            <button
              onClick={() => reopen.mutate()}
              disabled={reopen.isPending}
              title="Відкрити назад"
            >
              ↩ Відкрити
            </button>
          )}
          {!isNew && !isPosted && (
            <button
              className="danger"
              onClick={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
            >
              Видалити
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Тип документа</label>
          <select
            value={form.order_kind}
            onChange={(e) =>
              setForm({ ...form, order_kind: e.target.value as OrderKind })
            }
            disabled={!isNew || isPosted}
            data-testid="order-kind-select"
          >
            <option value="ORDER">
              {isCustomer ? 'Замовлення покупця' : 'Замовлення постачальнику'}
            </option>
            <option value="PROFORMA_INVOICE">
              {isCustomer ? 'Рахунок на оплату' : 'Рахунок від постачальника'}
            </option>
          </select>
        </div>
        <div className="field">
          <label>Організація *</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
            style={{
              borderColor: isNew && !form.organization_id ? '#d33' : undefined,
            }}
          >
            <option value="">— оберіть юрособу —</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && !form.organization_id && !permsLoading && (
            <small style={{ color: '#d33', fontSize: '0.75rem' }}>
              {availableOrgs.length === 0 && !hasPermissionInAnyOrg
                ? `У вас немає права «${requiredPerm}» у жодній юрособі`
                : 'Спочатку оберіть юрособу'}
            </small>
          )}
          {isNew && availableOrgs.length < totalAccessibleOrgs && availableOrgs.length > 0 && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>
            № документа{' '}
            {isNew && (
              <small style={{ color: 'var(--muted)' }}>
                (auto, якщо порожнє)
              </small>
            )}
          </label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            disabled={isPosted}
            placeholder={
              isNew
                ? (form.order_kind === 'PROFORMA_INVOICE'
                    ? (isCustomer ? 'РО-0001-2026 (auto)' : 'РП-0001-2026 (auto)')
                    : (isCustomer ? 'ЗП-0001-2026 (auto)' : 'ЗС-0001-2026 (auto)'))
                : undefined
            }
          />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Термін поставки</label>
          <DatePopover value={form.delivery_date}
                 onChange={(v) => setForm({ ...form, delivery_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      {/* ПРОДАЖ-розбивка Етап 1 (2026-06-03): Inkoterms + Payment terms */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Умови постачання (Інкотермс)</label>
          <select
            value={form.incoterms}
            onChange={(e) => setForm({ ...form, incoterms: e.target.value })}
            disabled={isPosted}
          >
            <option value="">— Не вказано —</option>
            {INCOTERMS_OPTIONS.map((it) => (
              <option key={it} value={it}>{it}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Умови оплати</label>
          <select
            value={form.payment_terms_type}
            onChange={(e) => {
              const ptt = e.target.value as FormState['payment_terms_type'];
              setForm({
                ...form,
                payment_terms_type: ptt,
                prepayment_percent: ptt === 'PARTIAL' ? form.prepayment_percent : '',
              });
            }}
            disabled={isPosted}
          >
            <option value="">— Не вказано —</option>
            {(Object.keys(PAYMENT_TERMS_LABELS) as Array<keyof typeof PAYMENT_TERMS_LABELS>).map((pt) => (
              <option key={pt} value={pt}>{PAYMENT_TERMS_LABELS[pt]}</option>
            ))}
          </select>
        </div>
      </div>
      {(form.payment_terms_type === 'PARTIAL' || form.payment_terms_type === 'POSTPAY' || form.payment_terms_type === 'PREPAY') && (
        <div style={{ display: 'grid', gridTemplateColumns: form.payment_terms_type === 'PARTIAL' ? '1fr 1fr' : '1fr', gap: '0.75rem' }}>
          {form.payment_terms_type === 'PARTIAL' && (
            <div className="field">
              <label>Розмір передоплати, % <span style={{ color: 'red' }}>*</span></label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="99.99"
                value={form.prepayment_percent}
                onChange={(e) => setForm({ ...form, prepayment_percent: e.target.value })}
                disabled={isPosted}
                placeholder="наприклад 30"
              />
            </div>
          )}
          <div className="field">
            <label>Строк кінцевого розрахунку, роб. днів</label>
            <input
              type="number"
              min="0"
              value={form.final_payment_days}
              onChange={(e) => setForm({ ...form, final_payment_days: e.target.value })}
              disabled={isPosted}
              placeholder="наприклад 14"
            />
          </div>
        </div>
      )}
      {/* Due now amount — обчислюється локально для UX (backend computed_field теж дасть). */}
      {form.payment_terms_type && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem',
          background: 'var(--bg-soft)',
          borderRadius: 'var(--radius)',
          fontSize: '0.9rem',
        }}>
          <strong>Сума «До сплати»: </strong>
          {(() => {
            const total = totalAmount;
            if (form.payment_terms_type === 'PREPAY') return fmt(total) + ' (100%)';
            if (form.payment_terms_type === 'PARTIAL' && form.prepayment_percent) {
              const dueNow = total * Number(form.prepayment_percent) / 100;
              return `${fmt(dueNow)} (${form.prepayment_percent}% від ${fmt(total)})`;
            }
            if (form.payment_terms_type === 'POSTPAY') return '0.00 (постоплата)';
            if (form.payment_terms_type === 'ON_DELIVERY') return '0.00 (при отриманні)';
            return fmt(total);
          })()}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{partyLabel}</label>
          <select value={form.contractor_id}
                  onChange={(e) => setForm({ ...form, contractor_id: e.target.value, contract_id: '' })}
                  disabled={isPosted}>
            <option value="">—</option>
            {filteredContractors.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>
            Договір{' '}
            <small style={{ color: 'var(--muted)' }}>
              (необов'язково — буде створено основний автоматично)
            </small>
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.contractor_id}>
            <option value="">без договору</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Склад</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Рядки</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Номенклатура</th>
            <th style={{ padding: '0.5rem', width: 90 }}>К-сть</th>
            <th style={{ padding: '0.5rem', width: 110 }}>Ціна</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Сума без ПДВ</th>
            <th style={{ padding: '0.5rem', width: 100 }}>ПДВ</th>
            <th style={{ padding: '0.5rem', width: 110 }}>ПДВ грн</th>
            <th style={{ padding: '0.5rem', width: 120 }}>Усього</th>
            {!isNew && <th style={{ padding: '0.5rem', width: 100 }}>{progressLabel}</th>}
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={it.nomenclature_id}
                        onChange={(e) => setItem(i, { nomenclature_id: e.target.value })}
                        disabled={isPosted}>
                  <option value="">—</option>
                  {nomList.data?.items.map((n) => (
                    <option key={n.id} value={n.id}>{n.code} — {n.name}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.001" value={it.quantity}
                       onChange={(e) => setItem(i, { quantity: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.01" value={it.price}
                       onChange={(e) => setItem(i, { price: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(Number(it.amount_without_vat))}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={it.vat_rate_id}
                        onChange={(e) => setItem(i, { vat_rate_id: e.target.value })}
                        disabled={isPosted}>
                  {vatList.data?.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(Number(it.vat_amount))}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmt(Number(it.total_amount))}</td>
              {!isNew && (
                <td style={{ padding: '0.5rem', textAlign: 'right', color: '#64748b' }}>
                  {Number(it[progressColumn] ?? 0).toLocaleString('uk-UA',
                    { minimumFractionDigits: 0, maximumFractionDigits: 4 })}
                </td>
              )}
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger" style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={4} style={{ padding: '0.5rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalWithoutVat)}</td>
            <td></td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalVat)}</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalAmount)}</td>
            {!isNew && <td></td>}
            <td></td>
          </tr>
        </tfoot>
      </table>
      {!isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>
      )}
      <div className="field">
        <label>Проект / напрям</label>
        <ProjectSelect
          value={form.project_id}
          organizationId={form.organization_id}
          onChange={(v) => setForm({ ...form, project_id: v })}
          disabled={isPosted} />
      </div>

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          {isCustomer
            ? 'Замовлення покупця: бухгалтерських проводок не створює, лише записує до регістру замовлень для відстеження виконання. При відвантаженні з посиланням на це замовлення регістр автоматично закриється.'
            : 'Замовлення постачальнику: бухгалтерських проводок не створює. При надходженні товару з посиланням на це замовлення поле «Отримано» в рядках оновиться автоматично.'}
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as {
      response?: {
        data?: {
          detail?: string | Array<{ loc?: unknown[]; msg?: string }>
        }
      }
    }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    // Pydantic 422: detail — array з обʼєктів {type, loc, msg, input, ctx}
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1])
          : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}
